import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Sparkles, Send, X, Loader2, ShoppingBag } from "lucide-react";
import VoiceSearchButton from "@/components/VoiceSearchButton";

const SUGGESTIONS = [
  "Outfit for a wedding in Manchester",
  "Red Ankara dress under £40",
  "A caterer for a party in London",
  "Gift ideas for my mum",
];

const ResultCard = ({ listing }) => {
  const { formatPriceDual } = useCurrency();
  const dual = formatPriceDual(listing.price, listing.currency);
  const img = listing.images?.[0] || "https://images.unsplash.com/photo-1606744824163-985d376605aa?auto=format&fit=crop&q=80&w=400";
  return (
    <Link
      to={`/listing/${listing.listing_id}`}
      data-testid={`shopper-result-${listing.listing_id}`}
      className="flex gap-3 items-center p-2 rounded-lg border border-black/10 bg-white hover:border-[#C05A3C]/50 hover:shadow-sm transition-all"
    >
      <img src={img} alt={listing.title} className="w-14 h-14 rounded-md object-cover shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-[#1A2024] truncate">{listing.title}</div>
        <div className="text-xs text-muted-foreground truncate">{listing.category}{listing.city ? ` · ${listing.city}` : ""}</div>
        <div className="text-sm font-semibold text-[#C05A3C]">{dual.primary || `${listing.currency || ""} ${listing.price}`}</div>
      </div>
    </Link>
  );
};

export const AIShopperPanel = ({ embedded = false }) => {
  const [messages, setMessages] = useState([
    { role: "assistant", reply: "Hi 👋 I'm your AfriMarket personal shopper. Tell me what you're looking for — a product, a service, or an occasion like \"a wedding in Manchester\" — and I'll find it for you.", groups: [] },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    const history = messages
      .filter((m) => m.reply)
      .map((m) => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.reply }));
    setMessages((m) => [...m, { role: "user", reply: msg, groups: [] }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/shopper", { message: msg, session_id: sessionId, history });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", reply: data.reply, groups: data.groups || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", reply: "Sorry, I had trouble with that. Please try again.", groups: [] }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? "flex flex-col h-full" : "flex flex-col h-[70vh] max-h-[600px]"} data-testid="ai-shopper-panel">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
            <div className={m.role === "user"
              ? "bg-[#1A2024] text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[85%] text-sm"
              : "bg-[#FAF7F2] text-[#1A2024] rounded-2xl rounded-bl-sm px-4 py-2 max-w-[92%] text-sm border border-black/5"}>
              {m.reply}
            </div>
            {m.role === "assistant" && m.groups?.length > 0 && (
              <div className="mt-3 space-y-4">
                {m.groups.map((g, gi) => (
                  <div key={gi}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#C05A3C] mb-1.5 flex items-center gap-1.5">
                      <ShoppingBag className="w-3.5 h-3.5" />{g.label}
                    </div>
                    <div className="space-y-2">
                      {g.listings.map((l) => <ResultCard key={l.listing_id} listing={l} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="shopper-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Finding the best picks…
          </div>
        )}
        {messages.length <= 1 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-[#C05A3C]/30 text-[#C05A3C] hover:bg-[#C05A3C]/10 transition-colors" data-testid="shopper-suggestion">
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="p-3 border-t border-black/10 bg-white">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 items-center">
          <VoiceSearchButton
            onResult={(q) => send(q)}
            title="Speak your request"
            className="w-11 h-11 rounded-full p-0 shrink-0 border-[#C05A3C]/40 text-[#C05A3C]"
          />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type or tap the mic 🎙️"
            data-testid="shopper-input"
            className="flex-1 h-11 px-4 rounded-full border border-black/15 text-sm focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/30"
          />
          <button type="submit" disabled={loading || !input.trim()} data-testid="shopper-send" className="w-11 h-11 rounded-full bg-[#C05A3C] hover:bg-[#D46B4E] disabled:opacity-50 text-white grid place-items-center shrink-0">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};

const AIShopperBubble = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-shopper-bubble"
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-[#C05A3C] hover:bg-[#D46B4E] text-white shadow-lg shadow-black/20 transition-all hover:scale-105"
        >
          <Sparkles className="w-5 h-5" />
          <span className="text-sm font-semibold hidden sm:inline">AI Shopper</span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[400px] rounded-2xl bg-white shadow-2xl shadow-black/30 border border-black/10 overflow-hidden flex flex-col" data-testid="ai-shopper-window">
          <div className="flex items-center justify-between px-4 py-3 bg-[#1A2024] text-white">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Sparkles className="w-4 h-4 text-[#D9923B]" /> AI Personal Shopper
            </div>
            <button onClick={() => setOpen(false)} data-testid="shopper-close" className="p-1 hover:bg-white/10 rounded-md">
              <X className="w-4 h-4" />
            </button>
          </div>
          <AIShopperPanel />
        </div>
      )}
    </>
  );
};

export default AIShopperBubble;
